<template>
  <CRow>
    <CCol sm="12">
      <CCard>
        <CCardHeader>
          <CRow>
            <CCol sm="7">
              <CIcon name="cil-cloud-upload"/>
              <h5>Materialimport</h5>
            </CCol>
            <CCol sm="5" class="text-right">
              <CButton color="secondary" size="sm" @click="goToOverview">
                <CIcon name="cil-arrow-thick-left"/>
                Zur Materialübersicht
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>

        <CCardBody>
          <CRow class="mb-4">
            <CCol lg="6" class="mb-4 mb-lg-0">
              <div class="material-import-panel h-100">
                <h6 class="mb-2">Importdateien</h6>
                <p class="text-muted small mb-3">
                  Laden Sie eine DATANORM ZIP-Datei oder eine einzelne DATANORM.001 hoch. Eine DATPREIS.001 kann zusätzlich für Preise angegeben werden.
                </p>

                <CAlert v-if="runningManufacturerImports.length > 0" color="warning" show class="small">
                  Für <strong>{{ resolvedManufacturerName }}</strong> läuft bereits ein Import.
                  Ein neuer Start kann die Ergebnisse des laufenden Imports überschreiben.
                </CAlert>

                <FormError v-if="importError" :errormessage="importError"/>

                <CRow class="form-group">
                  <CCol sm="12" class="material-import-file material-import-file--datanorm">
                    <label class="font-weight-bold mb-1">DATANORM Datei</label>
                    <CInputFile
                      custom
                      name="materialImportDatanorm"
                      ref="datanormImportFile"
                      accept=".zip,.001"
                      placeholder="Datei auswählen..."
                      @change.native="handleDatanormFileUpload"
                    />
                  </CCol>
                </CRow>

                <CRow class="form-group">
                  <CCol sm="12" class="material-import-file material-import-file--datpreis">
                    <label class="font-weight-bold mb-1">DATPREIS Datei</label>
                    <CInputFile
                      custom
                      name="materialImportDatpreis"
                      ref="datpreisImportFile"
                      accept=".001"
                      placeholder="Datei auswählen..."
                      @change.native="handleDatpreisFileUpload"
                    />
                    <small class="text-muted d-block mt-2">
                      Optional für Preise. Erwarteter Dateiname: DATPREIS.001
                    </small>
                  </CCol>
                </CRow>

                <CRow class="form-group">
                  <CCol sm="12">
                    <label class="font-weight-bold mb-1">Hersteller</label>
                    <CSelect
                      :value.sync="selectedHerstellerId"
                      :options="herstellerOptions"
                      placeholder="Bitte einen Hersteller wählen"
                    />
                  </CCol>
                </CRow>

                <MaterialText
                  v-model="manualHerstellerName"
                  v-if="false"
                  form-label="Herstellername überschreiben"
                  placeholder="Optionaler Herstellername für den Import"
                  rules=""
                />

                <CRow class="mt-4">
                  <CCol sm="12" class="text-right">
                    <CButton color="success" :disabled="!canImport || importSubmitting" @click="startImport">
                      <span v-if="importSubmitting" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      <CIcon v-else name="cil-library-add"/>
                      {{ importSubmitting ? 'Import wird gestartet...' : 'Import starten' }}
                    </CButton>
                  </CCol>
                </CRow>
              </div>
            </CCol>

            <CCol lg="6">
              <div class="material-import-panel h-100">
                <h6 class="mb-2">Aktueller Status</h6>
                <p class="text-muted small mb-3">
                  Laufende und wartende Importe werden automatisch aktualisiert.
                </p>

                <div v-if="activeImports.length === 0" class="text-muted small">
                  Aktuell laufen keine Materialimporte.
                </div>

                <div v-for="item in activeImports" :key="`active-import-${item.importid}`" class="material-import-status-row">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <div class="font-weight-bold">#{{ item.importid }} {{ item.filename }}</div>
                      <div class="text-muted small">{{ item.hersteller_name || '-' }}</div>
                    </div>
                    <CBadge :color="statusColor(item.status)">
                      <span v-if="isImportActive(item.status)" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      {{ statusLabel(item.status) }}
                    </CBadge>
                  </div>
                  <div class="text-muted small mt-2">
                    Angelegt: {{ item.created }}<span v-if="item.started_at !== '-'"> | Gestartet: {{ item.started_at }}</span>
                    <span v-if="item.total_records"> | Fortschritt: {{ item.processed_records }}/{{ item.total_records }}</span>
                  </div>
                </div>
              </div>
            </CCol>
          </CRow>

          <CCard class="mb-0">
            <CCardHeader>
              <CIcon name="cil-history"/>
              <h5>Importhistorie</h5>
            </CCardHeader>
            <CCardBody>
              <CDataTable
                :items="importItems"
                :fields="importFields"
                hover
                :items-per-page-select="{label: 'Einträge pro Seite'}"
                :items-per-page="10"
                column-filter
                sorter
                pagination
              >
                <template #no-items-view><NoData/></template>

                <template #status="{item}">
                  <td>
                    <CBadge :color="statusColor(item.status)">
                      <span v-if="isImportActive(item.status)" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      {{ statusLabel(item.status) }}
                    </CBadge>
                  </td>
                </template>

                <template #error_text="{item}">
                  <td>{{ item.error_text || '-' }}</td>
                </template>

                <template #progress="{item}">
                  <td>{{ progressLabel(item) }}</td>
                </template>

                <template #actionbar="{item}">
                  <td class="text-right">
                    <CButton color="light" size="sm" :disabled="importSubmitting" @click="reuploadImport(item)">
                      Erneut einreihen
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>
          </CCard>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Vue from 'vue'
import MaterialText from '@/components/FormElements/Text'
import FormError from '@/components/FormError'
import NoData from '@/components/NoData'

export default {
  name: 'MaterialImport',
  components: {
    FormError,
    MaterialText,
    NoData
  },
  data () {
    return {
      imports: [],
      hersteller: [],
      importUploadFile: null,
      datpreisUploadFile: null,
      selectedHerstellerId: '',
      manualHerstellerName: '',
      importError: null,
      importSubmitting: false,
      refreshTimerId: null,
      importFields: [
        { key: 'importid', label: 'ID' },
        { key: 'filename', label: 'Datei' },
        { key: 'datpreis_filename', label: 'Preisdatei' },
        { key: 'hersteller_name', label: 'Hersteller' },
        { key: 'status', label: 'Status' },
        { key: 'progress', label: 'Fortschritt' },
        { key: 'products_added', label: 'Neu' },
        { key: 'products_updated', label: 'Aktualisiert' },
        { key: 'created', label: 'Angelegt' },
        { key: 'started_at', label: 'Gestartet' },
        { key: 'finished_at', label: 'Beendet' },
        { key: 'error_at', label: 'Fehler am' },
        { key: 'error_text', label: 'Hinweis' },
        { key: 'actionbar', label: '', sorter: false, filter: false }
      ]
    }
  },
  computed: {
    activeImports () {
      return this.importItems.filter(item => this.isImportActive(item.status))
    },
    canImport () {
      return Boolean(this.importUploadFile) && this.resolvedManufacturerName !== ''
    },
    herstellerOptions () {
      const baseOption = [{ value: '', label: 'Bitte einen Hersteller wählen' }]
      return baseOption.concat(this.hersteller.map(item => ({
        value: item.herstellerid,
        label: item.hersteller
      })))
    },
    importItems () {
      return this.imports.map(item => ({
        importid: item.importid,
        filename: item.filename,
        datpreis_filename: item.datpreis_filename || '-',
        herstellerid: item.herstellerid || '',
        hersteller_name: item.hersteller_name || '-',
        status: item.status,
        processed_records: Number(item.processed_records || 0),
        total_records: item.total_records !== null && item.total_records !== undefined ? Number(item.total_records) : null,
        products_added: item.products_added,
        products_updated: item.products_updated,
        created: this.formatDate(item.created),
        started_at: this.formatDate(item.started_at),
        finished_at: this.formatDate(item.finished_at),
        error_at: this.formatDate(item.error_at),
        error_text: item.error_text ? item.error_text.substring(0, 160) : ''
      }))
    },
    resolvedManufacturerName () {
      if (this.manualHerstellerName.trim() !== '') {
        return this.manualHerstellerName.trim()
      }

      const selected = this.hersteller.find(item => String(item.herstellerid) === String(this.selectedHerstellerId))
      return selected ? selected.hersteller.trim() : ''
    },
    runningManufacturerImports () {
      const resolvedManufacturerName = this.resolvedManufacturerName.toLowerCase()
      if (resolvedManufacturerName === '') {
        return []
      }

      return this.importItems.filter(item => {
        if (!this.isImportActive(item.status)) {
          return false
        }

        const manufacturerNameMatches = (item.hersteller_name || '').toLowerCase() === resolvedManufacturerName
        const manufacturerIdMatches = this.selectedHerstellerId !== '' && String(item.herstellerid) === String(this.selectedHerstellerId)
        return manufacturerNameMatches || manufacturerIdMatches
      })
    }
  },
  mounted () {
    this.loadHersteller()
    this.loadImports()
    this.refreshTimerId = window.setInterval(() => {
      this.loadImports()
    }, 15000)
  },
  beforeDestroy () {
    if (this.refreshTimerId) {
      window.clearInterval(this.refreshTimerId)
    }
  },
  methods: {
    formatDate (value) {
      if (!value) {
        return '-'
      }

      return value.replace('T', ' ').substring(0, 16)
    },
    goToOverview () {
      this.$router.push({ path: '/material/uebersicht' })
    },
    handleDatpreisFileUpload (event) {
      const files = event.target.files || []
      this.datpreisUploadFile = files.length > 0 ? files[0] : null
      this.importError = null
      this.updateFileLabel(this.$refs.datpreisImportFile, this.datpreisUploadFile ? this.datpreisUploadFile.name : 'Datei auswählen...')
    },
    handleDatanormFileUpload (event) {
      const files = event.target.files || []
      this.importUploadFile = files.length > 0 ? files[0] : null
      this.importError = null
      this.updateFileLabel(this.$refs.datanormImportFile, this.importUploadFile ? this.importUploadFile.name : 'Datei auswählen...')
    },
    isImportActive (status) {
      return status === 'queued' || status === 'running'
    },
    loadHersteller () {
      Vue.axios.get('/hersteller/get')
        .then((response) => {
          this.hersteller = response.data
        })
    },
    loadImports () {
      Vue.axios.get('/material/import/get')
        .then((response) => {
          this.imports = response.data
        })
    },
    progressLabel (item) {
      if (!item) {
        return '-'
      }

      if (item.total_records === null || item.total_records === undefined || item.total_records === 0) {
        return item.processed_records > 0 ? `${item.processed_records}` : '-'
      }

      return `${item.processed_records}/${item.total_records}`
    },
    resetImportForm () {
      this.importUploadFile = null
      this.datpreisUploadFile = null
      this.selectedHerstellerId = ''
      this.manualHerstellerName = ''
      this.importError = null
      this.updateFileLabel(this.$refs.datanormImportFile, 'Datei auswählen...')
      this.updateFileLabel(this.$refs.datpreisImportFile, 'Datei auswählen...')
    },
    startImport () {
      if (!this.canImport || this.importSubmitting) {
        return
      }

      if (this.runningManufacturerImports.length > 0) {
        const options = { okText: 'Trotzdem starten', cancelText: 'Abbrechen' }
        this.$dialog.confirm(`Für ${this.resolvedManufacturerName} läuft bereits ein Import. Der neue Upload kann dessen Ergebnisse überschreiben. Trotzdem fortfahren?`, options)
          .then(() => {
            this.submitImport()
          })
          .catch(() => {})
        return
      }

      this.submitImport()
    },
    statusColor (status) {
      if (status === 'finished') {
        return 'success'
      }
      if (status === 'failed') {
        return 'danger'
      }
      if (status === 'running') {
        return 'warning'
      }
      return 'info'
    },
    statusLabel (status) {
      if (status === 'finished') {
        return 'Beendet'
      }
      if (status === 'failed') {
        return 'Fehler'
      }
      if (status === 'running') {
        return 'Läuft'
      }
      return 'Wartet'
    },
    reuploadImport (item) {
      if (!item || !item.importid || this.importSubmitting) {
        return
      }

      this.importSubmitting = true
      this.importError = null

      Vue.axios.post(`/material/import/reupload/${item.importid}`)
        .then(() => {
          this.$snotify.success('Der Import wurde erneut in die Warteschlange gelegt.', {
            position: 'rightTop',
            timeout: 4000
          })
          this.loadImports()
        }).catch((error) => {
          this.importError = error.response && error.response.data
            ? (error.response.data.message || error.response.data)
            : 'Der Import konnte nicht erneut angestoßen werden.'
        }).finally(() => {
          this.importSubmitting = false
        })
    },
    submitImport (forceOverwrite = false) {
      this.importSubmitting = true
      this.importError = null

      const formData = new FormData()
      formData.append('uploadfile', this.importUploadFile)
      if (this.datpreisUploadFile) {
        formData.append('datpreisfile', this.datpreisUploadFile)
      }
      if (this.selectedHerstellerId) {
        formData.append('herstellerid', this.selectedHerstellerId)
      }
      if (this.manualHerstellerName.trim() !== '') {
        formData.append('hersteller_name', this.manualHerstellerName.trim())
      }
      if (forceOverwrite) {
        formData.append('force_overwrite', '1')
      }

      Vue.axios.post('/material/import/datanorm', formData, {
        headers: {
          'content-type': 'multipart/form-data'
        }
      }).then(() => {
        this.$snotify.success('Der Materialimport wurde in die Warteschlange gelegt.', {
          position: 'rightTop',
          timeout: 4000
        })
        this.resetImportForm()
        this.loadImports()
      }).catch((error) => {
        if (error.response && error.response.status === 409) {
          this.importSubmitting = false
          const warningMessage = error.response.data && error.response.data.message
            ? error.response.data.message
            : `Für ${this.resolvedManufacturerName} läuft bereits ein Import.`
          const options = { okText: 'Trotzdem starten', cancelText: 'Abbrechen' }
          this.$dialog.confirm(`${warningMessage} Trotzdem fortfahren?`, options)
            .then(() => {
              this.submitImport(true)
            })
            .catch(() => {})
          return
        }

        this.importError = error.response && error.response.data
          ? (error.response.data.message || error.response.data)
          : 'Der Import konnte nicht gestartet werden.'
      }).finally(() => {
        this.importSubmitting = false
      })
    },
    updateFileLabel (inputRef, text) {
      if (!inputRef || !inputRef.$el) {
        return
      }

      const label = inputRef.$el.parentNode.querySelector('label.custom-file-label')
      if (label) {
        label.innerHTML = text
      }
    }
  }
}
</script>

<style lang="scss">
.material-import-panel {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 1.25rem;
  background: #fff;
}

.material-import-status-row {
  border: 1px solid #e3e5e8;
  border-radius: 0.25rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
  background: #fafbfc;
}

.material-import-file--datanorm .custom-file-input ~ .custom-file-label::after {
  content: 'DATANORM Datei';
}

.material-import-file--datpreis .custom-file-input ~ .custom-file-label::after {
  content: 'DATPREIS Datei';
}

.material-import-file .custom-file-label {
  overflow: hidden;
}
</style>
